// Add Chrome extension type declaration
declare global {
	interface Window {
		chrome?: {
			runtime: {
				sendMessage: (extensionId: string, message: any, callback: (response: any) => void) => void;
				lastError?: Error;
			};
		};
	}
}

import { useState, useEffect } from "react";
import { ModalForm } from "@/components/ModalForm";
import { CandidateFormFields } from "./CandidateFormFields";
import { CandidateFormProgress } from "./CandidateFormProgress";
import { Candidate } from "@/types/candidate";
import { createCandidate, CandidateFormData } from "@/utils/candidate";
import { extractInfoFromCV } from "@/utils/candidate/cv-extractor";
import { CVUploadZone } from "./CVUploadZone";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ProcessingOverlay } from "@/components/ui/ProcessingOverlay";

interface AddCandidateFormProps {
	isOpen: boolean;
	onClose: () => void;
	onCandidateAdded: (candidate: Candidate) => void;
	initialFormData?: CandidateFormData | null;
}

export const AddCandidateForm = ({ 
	isOpen, 
	onClose,
	onCandidateAdded,
	initialFormData = null
}: AddCandidateFormProps) => {
	const { t } = useTranslation();
	const location = useLocation();
	const [formData, setFormData] = useState<CandidateFormData>({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		role: "",
		skills: "",
		summary: "",
		cvFile: null,
		linkedin: ""
	});
	
	const [loading, setLoading] = useState(false);
	const [processingStage, setProcessingStage] = useState<string | null>(null);
	const [progress, setProgress] = useState(0);
	const [fetchingLinkedInData, setFetchingLinkedInData] = useState(false);
	
	// Apply initial form data when provided
	useEffect(() => {
		console.log('[AddCandidateForm] Received initialFormData:', initialFormData);
		if (initialFormData) {
			setFormData(initialFormData);
		}
	}, [initialFormData]);
	
	// Check for LinkedIn URL in the URL query parameters
	useEffect(() => {
		if (isOpen) {
			const params = new URLSearchParams(location.search);
			const linkedinUrl = params.get("linkedinUrl");
			
			console.log('[AddCandidateForm] LinkedIn URL from params:', linkedinUrl);
			
			if (linkedinUrl && !initialFormData) {
				// Set the LinkedIn URL in the form
				setFormData(prev => ({
					...prev,
					linkedin: linkedinUrl
				}));
				
				// Fetch LinkedIn data from our edge function
				fetchLinkedInData(linkedinUrl);
			}
		}
	}, [isOpen, location.search, initialFormData]);

	// Function to fetch LinkedIn data using our edge function
	const fetchLinkedInData = async (profileUrl: string) => {
		try {
			setFetchingLinkedInData(true);
			setProcessingStage("Fetching LinkedIn profile data...");
			setProgress(20);
			
			console.log("[AddCandidateForm] Fetching LinkedIn data for:", profileUrl);
			
			// Call our Edge Function to get the LinkedIn data
			const { data, error } = await supabase.functions.invoke('fetch-linkedin-profile', {
				body: { profileUrl },
				headers: {
					'Content-Type': 'application/json'
				}
			});
			
			console.log("[AddCandidateForm] Edge function response:", data);
			
			if (error) {
				console.error("[AddCandidateForm] Error fetching LinkedIn profile:", error);
				toast({
					title: t("candidates.newCandidateModal.linkedinDataError"),
					description: error.message || "Failed to fetch LinkedIn data",
					variant: "destructive"
				});
				setFetchingLinkedInData(false);
				setProcessingStage(null);
				setProgress(0);
				return;
			}
			
			setProgress(70);
			
			if (data && data.success && data.data) {
				console.log("[AddCandidateForm] LinkedIn data received:", data.data);
				const profileData = data.data;
				
				// Update form data with LinkedIn information
				const updatedData = {
					...formData,
					firstName: profileData.firstName || formData.firstName,
					lastName: profileData.lastName || formData.lastName,
					email: profileData.email || formData.email,
					phone: profileData.phone || formData.phone,
					role: profileData.role || formData.role,
					skills: profileData.skills || formData.skills,
					summary: profileData.summary || formData.summary,
					linkedin: profileData.linkedin || profileUrl,
					linkedinJson: profileData.linkedinJson
				};
				
				console.log("[AddCandidateForm] Setting form data with LinkedIn info:", updatedData);
				setFormData(updatedData);
				
				toast({
					title: t("candidates.newCandidateModal.linkedinDataReceived"),
					description: t("candidates.newCandidateModal.linkedinDataSuccess")
				});
			} else {
				console.error("[AddCandidateForm] Invalid or missing data structure from LinkedIn API:", data);
				toast({
					title: t("candidates.newCandidateModal.linkedinDataError"),
					description: "LinkedIn data structure was not as expected",
					variant: "destructive"
				});
			}
		} catch (error) {
			console.error("[AddCandidateForm] Error fetching LinkedIn data:", error);
			toast({
				title: t("candidates.newCandidateModal.linkedinDataError"),
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive"
			});
		} finally {
			setFetchingLinkedInData(false);
			setProcessingStage(null);
			setProgress(0);
		}
	};

	// Reset form when closed
	useEffect(() => {
		if (!isOpen && !initialFormData) {
			setFormData({
				firstName: "",
				lastName: "",
				email: "",
				phone: "",
				role: "",
				skills: "",
				summary: "",
				cvFile: null,
				linkedin: ""
			});
		}
	}, [isOpen, initialFormData]);
	
	const handleChange = (field: keyof CandidateFormData, value: string) => {
		console.log("[AddCandidateForm] Field changed:", field, value);
		setFormData(prev => ({ ...prev, [field]: value }));
	};
	
	const handleFileChange = async (file: File | null) => {
		setFormData(prev => ({ ...prev, cvFile: file }));
		
		// If a CV is uploaded, try to extract information from it
		if (file) {
			try {
				setProcessingStage("Extracting information from CV");
				setProgress(20);
				
				const extractedInfo = await extractInfoFromCV(file, (progress, status) => {
					setProgress(progress);
					setProcessingStage(status);
				});
				
				console.log("[AddCandidateForm] Extracted CV information:", extractedInfo);
				
				if (extractedInfo) {
					// Update form with extracted information, but don't overwrite existing values
					const updatedFormData = {
						...formData,
						firstName: formData.firstName || extractedInfo.firstName || "",
						lastName: formData.lastName || extractedInfo.lastName || "",
						email: formData.email || extractedInfo.email || "",
						phone: formData.phone || extractedInfo.phone || "",
						role: formData.role || extractedInfo.role || "",
						skills: formData.skills || (extractedInfo.skills ? extractedInfo.skills.join(", ") : ""),
						summary: formData.summary || extractedInfo.summary || "",
						linkedin: formData.linkedin || extractedInfo.linkedin || "",
					};
					
					console.log("[AddCandidateForm] Updating form data with extracted info:", updatedFormData);
					setFormData(updatedFormData);
				}
			} catch (error) {
				console.error("[AddCandidateForm] Error extracting info from CV:", error);
				toast({
					title: "CV Processing Error",
					description: "Failed to extract information from CV",
					variant: "destructive"
				});
			} finally {
				setProcessingStage(null);
				setProgress(0);
			}
		}
	};
	
	const handleFetchLinkedInProfile = () => {
		if (formData.linkedin) {
			fetchLinkedInData(formData.linkedin);
		} else {
			toast({
				title: "LinkedIn URL Required",
				description: "Please enter a LinkedIn profile URL first",
				variant: "destructive"
			});
		}
	};
	
	const updateProgress = (stage: string | null, progress: number) => {
		setProcessingStage(stage);
		setProgress(progress);
	};
	
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		
		try {
			// Sanitize form data to prevent circular references
			const sanitizedFormData = {
				...formData,
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				phone: formData.phone,
				role: formData.role,
				skills: formData.skills,
				linkedin: formData.linkedin,
				summary: formData.summary ? String(formData.summary) : "",
				// Only include file reference, not the entire DOM element
				cvFile: formData.cvFile,
				// Include LinkedIn JSON data if available
				linkedinJson: formData.linkedinJson
			};
			
			console.log('[AddCandidateForm] Submitting form data with LinkedIn JSON:', {
				...sanitizedFormData,
				linkedinJson: sanitizedFormData.linkedinJson ? 'present' : 'missing'
			});
			
			// Combine first and last name for the database
			const candidateData = await createCandidate(
				{
					...sanitizedFormData,
					name: `${sanitizedFormData.firstName} ${sanitizedFormData.lastName}`.trim()
				}, 
				updateProgress
			);
			
			if (candidateData) {
				onCandidateAdded(candidateData);
				
				setFormData({
					firstName: "",
					lastName: "",
					email: "",
					phone: "",
					role: "",
					skills: "",
					summary: "",
					linkedin: "",
					cvFile: null
				});
				setProcessingStage(null);
				setProgress(0);
			}
		} finally {
			setLoading(false);
		}
	};
	
	return (
		<ModalForm
			title={t("candidates.newCandidateModal.title")}
			isOpen={isOpen}
			onClose={onClose}
			onSubmit={handleSubmit}
			submitText={loading || fetchingLinkedInData ? t("common.loading") : t("candidates.newCandidateModal.saveButton")}
			submitDisabled={loading || fetchingLinkedInData}
			cancelText={t("candidates.newCandidateModal.cancelButton")}
		>
			<ProcessingOverlay 
				isProcessing={loading || fetchingLinkedInData}
				progress={progress}
				status={processingStage}
				message={loading ? "Creating candidate..." : "Fetching LinkedIn data..."}
			/>
			<div className="space-y-4">
				<p className="text-gray-600 text-sm">{t("candidates.newCandidateModal.description")}</p>
				<CandidateFormFields 
					formData={formData}
					onChange={handleChange}
					onFileChange={handleFileChange}
					disabled={loading || fetchingLinkedInData}
					onFetchLinkedIn={handleFetchLinkedInProfile}
				/>
			</div>
		</ModalForm>
	);
};
